/**
 * §P.2 · El calendario del club.
 *
 * Tres decisiones vienen del contrato y conviene tenerlas a la vista:
 *
 * **Mes a mes.** El generador acepta 62 días como máximo —«un calendario generado
 * a un año vista es un calendario que nadie ha revisado»— y además es como se
 * piensa un calendario. Así que el selector es un mes, no un rango libre.
 *
 * **`skipped` no es un error.** El generador es idempotente por partida doble: el
 * índice único y la comprobación de lo movido a mano. En la segunda pasada del
 * mismo mes **todo** sale en `skipped`, y eso significa «ya estaban», no que algo
 * haya fallado. Pintarlo en rojo enseñaría a la gente a no volver a pulsarlo.
 *
 * **No se reordenan las clases.** Vienen hacia adelante, al contrario que el resto
 * de listados, porque un calendario se lee del futuro al pasado y quien lo abre
 * está mirando la semana que viene.
 */
import { useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fechaParaInput, fechaYHora } from '@/shared/ui/formato';
import type { Grupo } from '@/club/grupos/tipos';
import {
  hora,
  mesActual,
  mesCompleto,
  mesDesplazado,
  nombreDeMes,
  porDia,
  type Clase,
  type Generacion,
  type PaginaDeClases,
} from '@/club/calendario/tipos';

const DIA_LARGO = new Intl.DateTimeFormat('es-ES', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

const diaLegible = (dia: string): string => {
  const [a, m, d] = dia.split('-').map(Number);
  return DIA_LARGO.format(new Date(a!, m! - 1, d!));
};

export function Calendario({
  club,
  puedeGenerar,
  puedeEditarClase,
  suspendido,
  alAbrirGrupo,
}: {
  club: string;
  /** Generar es de dueño y administrativo. */
  puedeGenerar: boolean;
  /** Mover o cancelar un día sí lo puede el entrenador: es quien está en la puerta. */
  puedeEditarClase: boolean;
  suspendido: boolean;
  alAbrirGrupo: (grupoId: string) => void;
}) {
  const cache = useQueryClient();
  const [mes, setMes] = useState(mesActual);
  const [grupoId, setGrupoId] = useState('');
  const [generacion, setGeneracion] = useState<Generacion | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);
  const [claseAbierta, setClaseAbierta] = useState<Clase | null>(null);

  const { from, to } = mesCompleto(mes);

  const grupos = useQuery({
    queryKey: ['grupos', club, false],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).groups.get({
        query: {},
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Grupo[];
    },
  });

  const clases = useQuery({
    queryKey: ['clases', club, mes, grupoId],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).sessions.get({
        query: {
          limit: 200,
          from,
          to,
          ...(grupoId ? { group_id: grupoId } : {}),
        },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as PaginaDeClases;
    },
  });

  const generar = useMutation({
    mutationFn: async () => {
      const r = await api.v1.clubs({ club }).sessions.generate.post(
        { from, to, ...(grupoId ? { group_id: grupoId } : {}) },
        {
          /*
           * 🔒 Una clave **nueva por pulsación**, y esto tiene su historia.
           *
           * La primera versión usaba una clave fija por periodo —`generar-club-
           * from-to`— pensando que «generar septiembre» era una sola operación.
           * Es un malentendido de para qué sirve la cabecera: protege **un envío**
           * de sus reintentos —un doble clic, una respuesta que no llegó—, no
           * convierte una operación en irrepetible.
           *
           * La consecuencia era peor que no tenerla: al pulsar por segunda vez el
           * servidor devolvía **la respuesta guardada** y la pantalla decía «2
           * clases creadas» cuando no había creado ninguna. Reportaba un trabajo
           * que no se hizo.
           *
           * Con clave nueva, la segunda pulsación se ejecuta de verdad y contesta
           * `created: 0, skipped: 2`, que es lo que el contrato promete: «pulsadlo
           * sin miedo — en la segunda pasada todo sale en skipped». La
           * idempotencia de verdad la da el propio endpoint: el índice único y la
           * comprobación de lo movido a mano.
           */
          headers: { 'idempotency-key': crypto.randomUUID() },
        }
      );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as Generacion;
    },
    onSuccess: (r) => {
      setProblema(null);
      setGeneracion(r);
      void cache.invalidateQueries({ queryKey: ['clases', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const cambiarMes = (pasos: number) => {
    setMes((m) => mesDesplazado(m, pasos));
    setGeneracion(null);
    setProblema(null);
  };

  if (claseAbierta) {
    return (
      <DetalleDeClase
        club={club}
        clase={claseAbierta}
        puedeEditar={puedeEditarClase && !suspendido}
        alCerrar={() => setClaseAbierta(null)}
        alAbrirGrupo={alAbrirGrupo}
      />
    );
  }

  const dias = porDia(clases.data?.data ?? []);
  const sinPasarLista = (clases.data?.data ?? []).filter(
    (c) => c.status === 'held' && !c.roll_called_at
  ).length;

  return (
    <section>
      <header className="titulo-fila">
        <h1>Calendario</h1>
        {puedeGenerar && !suspendido && (
          <button
            type="button"
            disabled={generar.isPending}
            onClick={() => generar.mutate()}
          >
            {generar.isPending
              ? 'Generando…'
              : `Generar las clases de ${nombreDeMes(mes)}`}
          </button>
        )}
      </header>

      <div className="mes-barra">
        <button
          type="button"
          className="btn-plano"
          onClick={() => cambiarMes(-1)}
        >
          ← Anterior
        </button>
        <strong className="mes-nombre">{nombreDeMes(mes)}</strong>
        <button
          type="button"
          className="btn-plano"
          onClick={() => cambiarMes(1)}
        >
          Siguiente →
        </button>

        <label className="mes-grupo">
          <span className="sr">Grupo</span>
          <select
            value={grupoId}
            onChange={(e) => {
              setGrupoId(e.target.value);
              setGeneracion(null);
            }}
            disabled={!grupos.data}
          >
            <option value="">Todos los grupos</option>
            {(grupos.data ?? []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/*
        El resultado del generador. `skipped` en ámbar suave y con la palabra
        «ya estaban», nunca en rojo: es lo que pasa siempre en la segunda pasada.
      */}
      {generacion && (
        <div className="nota nota-bien" role="status">
          {generacion.created > 0 ? (
            <>
              <strong>
                {generacion.created}{' '}
                {generacion.created === 1 ? 'clase creada' : 'clases creadas'}
              </strong>
              {generacion.skipped > 0 && (
                <> y {generacion.skipped} que ya estaban.</>
              )}
            </>
          ) : generacion.skipped > 0 ? (
            <>
              <strong>Ya estaba todo.</strong> Las {generacion.skipped} clases
              del mes ya existían, así que no se ha creado ninguna. Puedes
              pulsarlo tantas veces como quieras: no duplica nada ni pisa lo que
              hayas movido a mano.
            </>
          ) : (
            <>
              <strong>No había nada que generar.</strong> Ningún grupo abierto
              tiene horario para este mes: el horario es lo que dice qué clases
              hay.
            </>
          )}
        </div>
      )}

      {problema && <PanelError problema={problema} />}

      {clases.isPending && (
        <Cargando filas={6} etiqueta="Cargando el calendario" />
      )}

      {clases.isError && (
        <PanelError
          problema={aProblema(clases.error)}
          reintentar={() => void clases.refetch()}
        />
      )}

      {/* La métrica madre: clases dadas sin lista pasada. */}
      {sinPasarLista > 0 && (
        <div className="nota">
          <strong>
            {sinPasarLista}{' '}
            {sinPasarLista === 1
              ? 'clase de este mes se dio sin pasar lista'
              : 'clases de este mes se dieron sin pasar lista'}
            .
          </strong>{' '}
          Es el dato que mejor predice que un club se va a dar de baja, así que
          conviene mirarlo antes de que se acumule.
        </div>
      )}

      {clases.data?.data.length === 0 && (
        <Vacio
          titulo={`No hay clases en ${nombreDeMes(mes)}`}
          explicacion="Las clases salen del horario de cada grupo. Si los grupos ya tienen horario, genera el mes; si no, ponles el horario primero."
          {...(puedeGenerar && !suspendido
            ? {
                accion: (
                  <button type="button" onClick={() => generar.mutate()}>
                    Generar las clases de {nombreDeMes(mes)}
                  </button>
                ),
              }
            : {})}
        />
      )}

      {dias.length > 0 && (
        <ol className="calendario">
          {dias.map(({ dia, clases: delDia }) => (
            <li key={dia}>
              <h2>{diaLegible(dia)}</h2>
              <ul>
                {delDia.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className="clase"
                      onClick={() => setClaseAbierta(c)}
                    >
                      <span className="clase-hora">
                        {hora(c.starts_at)}–{hora(c.ends_at)}
                      </span>
                      <span className="clase-grupo">{c.group_name ?? '—'}</span>
                      <EstadoDeClase clase={c} />
                    </button>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      )}

      {clases.data?.has_more && (
        <p className="tenue">
          Hay más clases este mes de las que caben en una página. Filtra por
          grupo para verlas todas.
        </p>
      )}
    </section>
  );
}

/**
 * El estado de una clase.
 *
 * `roll_called_at` gana a todo: una clase dada con lista pasada está cerrada, y
 * una dada sin lista es lo que hay que ver.
 */
function EstadoDeClase({ clase }: { clase: Clase }) {
  if (clase.status === 'cancelled') {
    return (
      <span className="pastilla pastilla-mal" title={clase.cancel_reason ?? ''}>
        Cancelada
      </span>
    );
  }

  if (clase.roll_called_at) {
    return <span className="pastilla pastilla-active">Lista pasada</span>;
  }

  if (clase.status === 'held') {
    return <span className="pastilla pastilla-paused">Sin pasar lista</span>;
  }

  return <span className="pastilla pastilla-left">Programada</span>;
}

/** El detalle de una clase: mover el día, o cancelarlo con su motivo. */
function DetalleDeClase({
  club,
  clase,
  puedeEditar,
  alCerrar,
  alAbrirGrupo,
}: {
  club: string;
  clase: Clase;
  puedeEditar: boolean;
  alCerrar: () => void;
  alAbrirGrupo: (grupoId: string) => void;
}) {
  const cache = useQueryClient();
  const [cancelando, setCancelando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [problema, setProblema] = useState<Problema | null>(null);

  // El `ETag` lo pide el `PATCH`, y viene del `GET` del detalle.
  const detalle = useQuery({
    queryKey: ['clase', club, clase.id],
    queryFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .sessions({ sessionId: clase.id })
        .get();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return {
        clase: r.data as unknown as Clase,
        etag: r.response?.headers?.get('etag') ?? null,
      };
    },
  });

  const cancelar = useMutation({
    mutationFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .sessions({ sessionId: clase.id })
        .patch(
          { status: 'cancelled', cancel_reason: motivo.trim() },
          {
            headers: detalle.data?.etag
              ? { 'if-match': detalle.data.etag }
              : {},
          }
        );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setProblema(null);
      setCancelando(false);
      void cache.invalidateQueries({ queryKey: ['clases', club] });
      void cache.invalidateQueries({ queryKey: ['clase', club, clase.id] });
      alCerrar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const c = detalle.data?.clase ?? clase;

  return (
    <section className="ficha">
      <header className="titulo-fila">
        <button type="button" className="btn-plano" onClick={alCerrar}>
          ← Calendario
        </button>
      </header>

      <h1>{c.group_name ?? 'Clase'}</h1>
      <p className="tenue">
        {/* `fechaParaInput` hace las dos ramas —`Date` y cadena— en un sitio. */}
        {diaLegible(fechaParaInput(c.session_date))} · {hora(c.starts_at)}–
        {hora(c.ends_at)}
      </p>

      <dl className="datos">
        <div>
          <dt>Estado</dt>
          <dd>
            <EstadoDeClase clase={c} />
          </dd>
        </div>
        <div>
          <dt>Lista pasada</dt>
          <dd>
            {c.roll_called_at ? fechaYHora(c.roll_called_at) : 'Todavía no'}
          </dd>
        </div>
        {c.cancel_reason && (
          <div>
            <dt>Motivo de la cancelación</dt>
            <dd>{c.cancel_reason}</dd>
          </div>
        )}
      </dl>

      <div className="acciones">
        <button
          type="button"
          className="btn-plano"
          onClick={() => alAbrirGrupo(c.group_id)}
        >
          Ver el grupo
        </button>

        {puedeEditar && c.status !== 'cancelled' && !cancelando && (
          <button
            type="button"
            className="btn-plano"
            onClick={() => setCancelando(true)}
          >
            Cancelar esta clase
          </button>
        )}
      </div>

      {cancelando && (
        <form
          className="hueco hueco-confirmar"
          onSubmit={(e) => {
            e.preventDefault();
            cancelar.mutate();
          }}
        >
          <h2>Cancelar la clase de este día</h2>
          <p>
            No toca el horario del grupo: las demás semanas siguen igual. Solo
            este día.
          </p>

          <label>
            Motivo
            <input
              required
              minLength={3}
              maxLength={200}
              autoFocus
              placeholder="El polideportivo está cerrado"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>

          {/* Por qué el motivo no es opcional. */}
          <p className="tenue">
            <strong>Esto lo leen las familias.</strong> El aviso de una clase
            cancelada es uno de los dos que se saltan el horario de silencio,
            así que llega al móvil a la hora que sea: conviene que diga algo
            útil.
          </p>

          {problema && <PanelError problema={problema} />}

          <div className="hueco-acciones">
            <button
              type="submit"
              className="btn-peligro"
              disabled={cancelar.isPending || motivo.trim().length < 3}
            >
              {cancelar.isPending
                ? 'Cancelando…'
                : 'Cancelar la clase y avisar'}
            </button>
            <button
              type="button"
              className="btn-plano"
              disabled={cancelar.isPending}
              onClick={() => {
                setCancelando(false);
                setProblema(null);
              }}
            >
              Dejarlo como está
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
